/**
 * Конструктор отчётов (АН-05): источник, поля, отбор, группировки, итоги, вид, период и дата среза.
 * Предпросмотр считается сервером в правах автора; сохранённый отчёт открывается по ссылке /reports/custom/<id>.
 */
import type { CustomReport, ListResponse, ViewConfig } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Eye, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useCan } from '../../lib/session';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ExportButtons } from './ExportButtons';
import { ResultView } from './ResultView';
import { sourcesQuery } from './useListView';
import { cleanConfig, defaultConfigFor, emptyConfig } from './util';
import { ViewEditor } from './ViewEditor';

export function BuilderPage() {
  const { id } = useSearch({ strict: false }) as { id?: string };
  const mayEdit = useCan('analytics.report.edit');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const sources = useQuery(sourcesQuery);
  const existing = useQuery({
    queryKey: ['reports', 'custom', id],
    queryFn: () => api<CustomReport>(`/reports/custom/${id}`),
    enabled: !!id,
  });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState('');
  const [shared, setShared] = useState(false);
  const [config, setConfig] = useState<ViewConfig>(emptyConfig());
  const [asOf, setAsOf] = useState('');
  const [preview, setPreview] = useState<ListResponse | null>(null);

  useCrumbs([{ label: 'Конструктор отчётов' }]);

  // Открытый на правку отчёт заполняет форму один раз
  const loaded = existing.data?.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: заполнение при загрузке отчёта
  useEffect(() => {
    const r = existing.data;
    if (!r) return;
    setName(r.name);
    setDescription(r.description ?? '');
    setSource(r.source);
    setShared(r.shared);
    setConfig(r.config);
  }, [loaded]);

  const src = sources.data?.find((s) => s.key === source);
  const cleaned = src ? cleanConfig(config, src) : null;

  const pickSource = (key: string) => {
    setSource(key);
    setPreview(null);
    const s = sources.data?.find((x) => x.key === key);
    setConfig(s ? defaultConfigFor(s) : emptyConfig());
  };

  const run = useMutation({
    mutationFn: () =>
      api<ListResponse>('/reports/custom/preview', {
        method: 'POST',
        body: { source, config: cleaned, page: 1, pageSize: 50, asOf: asOf || undefined },
      }),
    onSuccess: setPreview,
  });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        source,
        shared,
        config: cleaned,
      };
      return id && existing.data?.mine
        ? api<CustomReport>(`/reports/custom/${id}`, { method: 'PATCH', body })
        : api<CustomReport>('/reports/custom', { method: 'POST', body });
    },
    onSuccess: (r) => {
      toast('Отчёт сохранён');
      void qc.invalidateQueries({ queryKey: ['reports', 'custom'] });
      void navigate({ to: '/reports/custom/$id', params: { id: r.id } });
    },
  });

  if (sources.isPending || (id && existing.isPending)) return <Spinner />;
  if (sources.isError) return <ErrorBox error={sources.error} className="m-4" />;
  if (existing.isError) return <ErrorBox error={existing.error} className="m-4" />;

  return (
    <Page>
      <PageHeader
        title={id ? 'Правка отчёта' : 'Конструктор отчётов'}
        subtitle="Выберите источник и соберите отчёт: поля, отбор, группировки, итоги и вид. Данные берутся в ваших правах."
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <Panel title="Настройка">
          <div className="flex flex-col gap-4">
            <Field label="Название отчёта">
              <Input
                className="h-11 md:h-10"
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например, «Договоры по статусам»"
              />
            </Field>
            <Field label="Описание">
              <Textarea
                value={description}
                maxLength={2000}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <Field label="Источник данных" hint="Показаны источники, на которые у вас есть право.">
              <Select className="h-11 md:h-10" value={source} onChange={(e) => pickSource(e.target.value)}>
                <option value="">— выберите —</option>
                {sources.data.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.group}: {s.title}
                  </option>
                ))}
              </Select>
            </Field>
            {src ? (
              <>
                <ViewEditor src={src} config={config} onChange={setConfig} />
                <Field
                  label="Дата среза"
                  hint="Если пусто — на сегодня. Что именно пересчитывается на дату, сервер скажет в примечании."
                >
                  <Input
                    className="h-11 md:h-10"
                    type="date"
                    value={asOf}
                    onChange={(e) => setAsOf(e.target.value)}
                  />
                </Field>
                <Checkbox
                  className="min-h-11 items-center"
                  label="Общий отчёт"
                  description="Откроют по ссылке все, у кого есть право на данные источника."
                  checked={shared}
                  onChange={(e) => setShared(e.target.checked)}
                />
              </>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                icon={<Eye className="size-4" />}
                disabled={!src}
                loading={run.isPending}
                onClick={() => run.mutate()}
              >
                Показать
              </Button>
              <Button
                variant="primary"
                icon={<Save className="size-4" />}
                disabled={!src || !name.trim() || !mayEdit}
                title={mayEdit ? undefined : 'Нет права создавать и править отчёты'}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                {id && existing.data?.mine ? 'Сохранить изменения' : 'Сохранить отчёт'}
              </Button>
            </div>
            {run.error ? <ErrorBox error={run.error} /> : null}
            {save.error ? <ErrorBox error={save.error} /> : null}
          </div>
        </Panel>
        <Panel
          title="Предпросмотр"
          meta={preview ? `первые ${preview.rows.length} из ${preview.total}` : undefined}
          actions={
            src && cleaned ? (
              <ExportButtons source={src.key} config={cleaned} asOf={asOf} title={name || src.title} />
            ) : null
          }
          bodyClassName="p-0"
        >
          {preview && cleaned ? (
            <>
              {preview.asOfNote ? (
                <p className="border-b border-line px-4 py-2 text-[12px] text-ink-2">{preview.asOfNote}</p>
              ) : null}
              <ResultView list={preview} config={cleaned} fields={src?.fields} />
            </>
          ) : (
            <EmptyState title="Отчёт ещё не построен">
              Выберите источник, настройте поля и нажмите «Показать».
            </EmptyState>
          )}
        </Panel>
      </div>
    </Page>
  );
}
